/**
 * A person's picture, or their initials.
 *
 * Initials are the real fallback, not a placeholder: almost nobody has an
 * avatar, and a grey silhouette in every row makes a list of people look like a
 * list of strangers. The tint is derived from the id so the same person is the
 * same colour everywhere — mauve, the accent, never violet, which is the brand
 * and belongs to interactive surfaces.
 */

import React from 'react';
import type { Presence } from '../lib/relay/types';

const TINTS = ['var(--mauve-400)', 'var(--mauve-500)', 'var(--mauve-600)', 'var(--violet-400)'];

const PRESENCE_COLOUR: Record<Presence, string> = {
  online: 'var(--signal)',
  away: 'var(--warning-500)',
  busy: 'var(--danger-500)',
  offline: 'var(--slate-400)'
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0]![0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? '') : '';
  return (first + last).toUpperCase();
}

function tintFor(id: string): string {
  let sum = 0;
  for (let i = 0; i < id.length; i++) sum = (sum + id.charCodeAt(i)) % 997;
  return TINTS[sum % TINTS.length]!;
}

export function Avatar({
  person,
  size = 36,
  presence
}: {
  person: { id: string; name: string; avatarUrl?: string };
  size?: number;
  /** Drawn as a dot when given. Omit it where presence is not the point. */
  presence?: Presence;
}) {
  const body: React.ReactNode = person.avatarUrl ? (
    <img
      src={person.avatarUrl}
      alt=""
      width={size}
      height={size}
      style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
    />
  ) : (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        background: tintFor(person.id),
        color: '#fff',
        fontWeight: 600,
        fontSize: Math.round(size * 0.38)
      }}
    >
      {initials(person.name)}
    </span>
  );

  return (
    <span style={{ position: 'relative', display: 'inline-block', flex: '0 0 auto' }}>
      {body}
      {presence && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            right: -1,
            bottom: -1,
            width: Math.max(8, Math.round(size * 0.28)),
            height: Math.max(8, Math.round(size * 0.28)),
            borderRadius: '50%',
            background: PRESENCE_COLOUR[presence],
            boxShadow: '0 0 0 2px var(--app-panel)'
          }}
        />
      )}
    </span>
  );
}
